import React, { FC, memo } from 'react';
import { StyleProps } from 'src/lib/styleProps';
import { AvailabilityStatus, CourseEvent } from 'src/common/models/event';
import { useIsLoggedIn } from 'src/common/hooks/useIsLoggedIn';
import { Button, ButtonWithLoader } from 'src/common/components/Button';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { media } from 'src/config/breakpoints';
import { format } from 'src/lib/date';
import { useRegisterForTheEventMutator } from 'src/common/hooks/event/useRegisterForTheEventMutator';
import { useDispatch } from 'react-redux';
import { snackbarActions } from 'src/state/notification/notificationActions';
import { Course } from 'src/common/models/course';
import { CourseEventGuestAction } from 'src/public/components/course-page/CourseEventSelectPanel/CourseEventGuestAction';
import { JoinWebinarButton } from 'src/course/components/JoinWebinarButton';
import { CourseEventsPanelMenu } from 'src/public/components/course-page/CourseEventSelectPanel/CourseEventsPanelMenu';
import { OptionalFold } from 'src/lib/OptionalFold';

const RegistrationStatusContainer = styled.div`
  display: flex;
  align-items: center;

  ${media.w.min.px768(css`
    display: flex;
    flex-direction: column;
  `)}
`;

const RegistrationInformation = styled.div`
  font-weight: 700;
  width: 100%;

  ${media.w.min.px768(css`
    border-top: 1px solid ${colors.darkGrey};
    padding-top: 0.875rem;
  `)}
`;

export interface CourseEventRegisterBlockProps {
  course: Course;
  courseEvents: CourseEvent[];
  selectedCourseEvent: CourseEvent;
}

export const CourseEventActions: FC<StyleProps & CourseEventRegisterBlockProps> = memo(
  ({ course, courseEvents, selectedCourseEvent }) => {
    const { isLoggedIn } = useIsLoggedIn();
    const mutation = useRegisterForTheEventMutator();
    const dispatch = useDispatch();

    if (selectedCourseEvent.availabilityStatus === AvailabilityStatus.Available) {
      if (!isLoggedIn) {
        return <CourseEventGuestAction course={course} selectedCourseEvent={selectedCourseEvent} />;
      }

      return (
        <Button
          style={{ width: '100%' }}
          as={ButtonWithLoader}
          onClick={() => {
            mutation
              .mutateAsync({
                courseId: selectedCourseEvent.courseId,
                eventId: selectedCourseEvent.id,
              })
              .then(() => {
                dispatch(
                  snackbarActions.enqueueSnackbar({
                    message: 'Zgłoszenie zostało wysłane',
                    options: { variant: 'success' },
                  }),
                );
              });
          }}
          loading={mutation.isLoading}
        >
          {!mutation.isLoading && 'Zapisz się bezpłatnie'}
        </Button>
      );
    }

    if (selectedCourseEvent.webinarJoinUrl != null) {
      return (
        <JoinWebinarButton
          courseLike={course}
          eventLike={selectedCourseEvent}
          size="default"
          webinarUrl={selectedCourseEvent.webinarJoinUrl}
          style={{ width: '100%' }}
        />
      );
    }

    if (selectedCourseEvent.webinarJoinUrl == null) {
      return (
        <RegistrationStatusContainer>
          <RegistrationInformation>
            {selectedCourseEvent.availabilityStatus === AvailabilityStatus.Pending && (
              <>Dziękujemy, potwierdzimy Twój udział w&nbsp;szkoleniu mailowo.</>
            )}
            {selectedCourseEvent.availabilityStatus === AvailabilityStatus.Accepted && (
              <>Gratulacje! Znajdujesz się na liście uczestników szkolenia.</>
            )}
            {selectedCourseEvent.availabilityStatus === AvailabilityStatus.Full && (
              <>Niestety, zakończyliśmy już zapisy na to szkolenie</>
            )}
            {selectedCourseEvent.availabilityStatus === AvailabilityStatus.NotAvailable && (
              <>Niestety, zakończyliśmy już zapisy na to szkolenie</>
            )}
            {selectedCourseEvent.availabilityStatus === AvailabilityStatus.OtherEvent && (
              <OptionalFold
                value={
                  courseEvents.find(
                    (x) =>
                      x.availabilityStatus === AvailabilityStatus.Accepted ||
                      x.availabilityStatus === AvailabilityStatus.Pending,
                  )?.startDateTime
                }
                onSome={(date) => <>Jesteś zapisany na: {format(date, 'dd MMM')}</>}
              />
            )}
          </RegistrationInformation>

          <CourseEventsPanelMenu
            courseTitle={course.title}
            selectedCourseEvent={selectedCourseEvent}
          />
        </RegistrationStatusContainer>
      );
    }

    return <></>;
  },
);
CourseEventActions.displayName = 'CourseEventActions';
